import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { User } from "@shared/schema";
import { config, buildApiUrl, getAuthHeaders } from "@/lib/config";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLocation] = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    const authUrl = buildApiUrl('api/auth/user');
    // Verify token with backend
    fetch(authUrl, {
      method: 'GET',
      headers: getAuthHeaders()
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      }
      throw new Error('Token invalid');
    })
    .then((userData) => {
      // Handle different response formats
      let processedUser;
      if (typeof userData === 'string') {
        try {
          processedUser = JSON.parse(userData);
        } catch (e) {
          console.error('useAuth: Failed to parse user data:', e);
          throw new Error('Invalid user data format');
        }
      } else {
        processedUser = userData;
      }
      
      // Transform the response to match User schema
      const user = {
        id: processedUser.id,
        email: processedUser.email,
        firstName: processedUser.firstName,
        lastName: processedUser.lastName,
        role: processedUser.role,
        username: processedUser.username || processedUser.id // fallback for username
      };
      setUser(user);
    })
    .catch((error) => {
      console.warn('useAuth: Auth check failed, clearing token');
      localStorage.removeItem('authToken');
      setUser(null);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const apiUrl = buildApiUrl('api/login');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('authToken', data.token);
      setUser(data.user);
      setLocation('/dashboard');
      return data;
    } catch (error) {
      console.error('useAuth: Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(buildApiUrl('api/logout'), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('authToken');
      setUser(null);
      setLocation('/');
    }
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
  };
}